import Big from 'big.js';
import minBy from 'lodash/minBy';

export type DecimalRoundingMethod = 'ceil' | 'round' | 'floor';

export type DecimalRoundingPrecision =
  | 'fiveDollar'
  | 'dollar'
  | 'quarter'
  | 'nickel';

const ROUNDING_PRECISION_VALUES: Record<DecimalRoundingPrecision, number> = {
  fiveDollar: 5,
  dollar: 1,
  quarter: 0.25,
  nickel: 0.05,
};

export type DecimalRounding = {
  precision: DecimalRoundingPrecision;
  method: DecimalRoundingMethod;
};

export type AllocationRatio = {
  ratio: number;
  rounding?: DecimalRounding;
};

function applyRounding(amount: Big, rounding: DecimalRounding): Big {
  const roundingValue = new Big(ROUNDING_PRECISION_VALUES[rounding.precision]);
  const mod = amount.mod(roundingValue);

  if (rounding.method === 'ceil') {
    return mod.eq(0) ? amount : amount.plus(roundingValue).minus(mod);
  }

  if (rounding.method === 'floor') {
    return amount.minus(mod);
  }

  if (rounding.method === 'round') {
    return mod.times(2).gte(roundingValue)
      ? amount.plus(roundingValue).minus(mod)
      : amount.minus(mod);
  }

  return amount;
}

function distributeRemainder(
  results: Big[],
  allocations: AllocationRatio[],
  remainder: Big,
  unit: Big,
): Big[] {
  const newResults = [...results];

  const areAllAllocationsRounded = allocations.every(
    ({ rounding }) => rounding,
  );
  if (areAllAllocationsRounded) {
    const lastIndex = newResults.length - 1;
    const lastResult = newResults[lastIndex];
    const lastAllocation = allocations[lastIndex];

    if (lastAllocation.rounding && !remainder.eq(0)) {
      const adjustedAmount = lastResult.plus(remainder);
      const roundedAmount = applyRounding(
        adjustedAmount,
        lastAllocation.rounding,
      );
      newResults[lastIndex] = roundedAmount;
    }

    return newResults;
  }

  const positiveRemainder = remainder.gte(0);
  const steps = remainder.div(unit).abs().round(0, Big.roundDown).toNumber();
  for (let i = 0; i < steps; i++) {
    const nonRoundedResults = newResults
      .map((result, index) => ({ result, index }))
      .filter(({ index }) => !allocations[index].rounding);

    if (nonRoundedResults.length === 0) {
      break;
    }

    const target = positiveRemainder
      ? minBy(nonRoundedResults, ({ result }) => result.toNumber())
      : minBy(nonRoundedResults, ({ result }) => -result.toNumber());

    if (!target) break;

    newResults[target.index] = newResults[target.index].plus(
      positiveRemainder ? unit : unit.times(-1),
    );
  }

  return newResults;
}

export function allocate(
  amount: number,
  precision: number,
  allocations: AllocationRatio[],
): number[] {
  const amt = new Big(amount);
  const unit = new Big(10).pow(-precision);

  if (amt.lt(0)) {
    throw new Error('Cannot allocate negative amounts');
  }

  if (allocations.some((allocation) => allocation.ratio < 0)) {
    throw new Error('Cannot allocate negative ratios');
  }

  if (allocations.length === 0) {
    return [];
  }

  const totalRatio = allocations.reduce(
    (acc, allocation) => acc + allocation.ratio,
    0,
  );
  let remainder = amt;

  if (totalRatio === 0) {
    return allocations.map(() => 0);
  }

  const results: Big[] = [];

  for (const [index, allocation] of allocations.entries()) {
    if (remainder.lte(0)) {
      results[index] = new Big(0);

      continue;
    }

    const { ratio, rounding } = allocation;
    let share = amt
      .times(ratio)
      .div(totalRatio)
      .round(precision, Big.roundDown);

    if (rounding) {
      share = applyRounding(share, rounding);
    }

    if (!rounding) {
      share = share.gt(remainder) ? remainder : share;
    }

    results[index] = share;
    remainder = remainder.minus(results[index]);
  }

  const finalResults = distributeRemainder(
    results,
    allocations,
    remainder,
    unit,
  );

  return finalResults.map((r) => r.toNumber());
}
